import { DefaultQueryHookOptions } from 'utils/defaults'
import { PDFDesignLayerType } from '../../../../types'
import { useCallback } from 'react'
import {
  usePdfDesignLayerAsphaltMaterialsQuery,
  usePdfDesignLayerConcreteMaterialsQuery,
  usePdfDesignLayerDailyInspectionWorkTypesQuery,
  usePdfDesignLayerMaterialRequirementsQuery,
} from 'schema'
import { useOptions as useOptionsHook } from 'components/CIMSSelect/hooks/useOptions'
import { DefaultOptionType } from 'components/CIMSSelect/types'
import { alphabeticalCompare } from '@cims-online/shared'
import { usePDFDesignLayersContext } from '../../../../PDFDesignLayersContext'
import CIMSBadge from 'components/CIMSBadge'
import { useProjectContext } from 'context/ProjectContext'

export function useOptions(
  typename: keyof typeof PDFDesignLayerType
): Record<
  keyof typeof PDFDesignLayerType,
  { loading: boolean; options: DefaultOptionType[] }
> {
  const { projectId } = useProjectContext()

  const map = (
    usePDFDesignLayersContext().pdfDesignLayers.getSettings(typename)
      ?.recordSettings ?? []
  ).reduce(
    (map, { globalId, projectLayerIds }) =>
      map.set(globalId, projectLayerIds.length),
    new Map<string, number>()
  )
  const queryOptions = useCallback(
    (notSkip: keyof typeof PDFDesignLayerType) => ({
      skip: typename !== notSkip,
      variables: { projectId },
      ...DefaultQueryHookOptions,
    }),
    [projectId, typename]
  )
  const asphaltMaterials = usePdfDesignLayerAsphaltMaterialsQuery(
    queryOptions('AsphaltMaterial')
  )
  const concreteMaterials = usePdfDesignLayerConcreteMaterialsQuery(
    queryOptions('ConcreteMaterial')
  )
  const workTypes = usePdfDesignLayerDailyInspectionWorkTypesQuery(
    queryOptions('DailyInspectionWorkType')
  )
  const materialRequirements = usePdfDesignLayerMaterialRequirementsQuery(
    queryOptions('MaterialRequirement')
  )
  /**
   * @deprecated we should come up with a more sustainable solution to this. perhaps a transformer on our varchar column decorators to convert '' -> null and '' -> null
   * @expires 2026-01-01
   */
  const coalesce = (...strings: (string | null)[]) => {
    for (const s of strings) {
      if (s && s !== '') return s
    }
    return null
  }
  return {
    AsphaltMaterial: {
      loading: asphaltMaterials.loading,
      options: useOptionsHook({
        items: asphaltMaterials.data?.asphaltMaterials.records,
        sort: (a, b) =>
          alphabeticalCompare(
            coalesce(a.placementType, a.globalId),
            coalesce(b.placementType, b.globalId)
          ),
        label: ({ globalId, placementType }) => (
          <CIMSBadge count={map.get(globalId)}>
            {coalesce(placementType, globalId)}
          </CIMSBadge>
        ),
        value: 'globalId',
      }),
    },
    ConcreteMaterial: {
      loading: concreteMaterials.loading,
      options: useOptionsHook({
        items: concreteMaterials.data?.concreteMaterials.records,
        sort: (a, b) =>
          alphabeticalCompare(
            coalesce(a.placementType, a.globalId),
            coalesce(b.placementType, b.globalId)
          ),
        label: ({ globalId, placementType }) => (
          <CIMSBadge count={map.get(globalId)}>
            {coalesce(placementType, globalId)}
          </CIMSBadge>
        ),
        value: 'globalId',
      }),
    },
    DailyInspectionWorkType: {
      loading: workTypes.loading,
      options: useOptionsHook({
        items: workTypes.data?.dailyInspectionWorkTypes.records,
        sort: (a, b) => {
          return alphabeticalCompare(
            coalesce(a.shortName, a.name, a.globalId),
            coalesce(b.shortName, b.name, b.globalId)
          )
        },
        label: ({ globalId, name, shortName }) => (
          <CIMSBadge count={map.get(globalId)}>
            {coalesce(shortName, name, globalId)}
          </CIMSBadge>
        ),
        value: 'globalId',
      }),
    },
    MaterialRequirement: {
      loading: materialRequirements.loading,
      options: useOptionsHook({
        items: materialRequirements.data?.materialRequirements.records,
        sort: (a, b) =>
          alphabeticalCompare(
            coalesce(a.materialSection, a.globalId),
            coalesce(b.materialSection, b.globalId)
          ),
        label: ({ globalId, materialSection }) => (
          <CIMSBadge count={map.get(globalId)}>
            {coalesce(materialSection, globalId)}
          </CIMSBadge>
        ),
        value: 'globalId',
      }),
    },
  }
}
